import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type Identifier,
  useCanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Lock, LockOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";

import type { Sale, ZoomMeetingAccess } from "../types";

type PermissionMeeting = { id: Identifier; restricted?: boolean };

/**
 * Who can see a meeting: everyone, or only the people chosen here (admins see
 * every meeting regardless). Admins only; anyone else gets nothing rendered, and
 * RLS refuses them anyway. The same control is the list's Permission cell and
 * sits beside the meeting page's title. LCCRM-93.
 */
export const MeetingPermission = ({
  meeting,
  className,
}: {
  meeting: PermissionMeeting;
  className?: string;
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const { canAccess } = useCanAccess({
    resource: "zoom_meeting_access",
    action: "edit",
  });
  if (!canAccess) return null;

  const restricted = meeting.restricted === true;
  // Icon only: locked or open. The state is in the tooltip and the accessible
  // name, so a screen reader still hears it.
  const Icon = restricted ? Lock : LockOpen;
  const label = translate(
    restricted
      ? "resources.zoom_meetings.permission.restricted"
      : "resources.zoom_meetings.permission.everyone",
  );

  return (
    // In the list the row navigates on click, and React carries clicks out of
    // the dialog's portal up to it, so nothing here may reach the row.
    <span className={className} onClick={(event) => event.stopPropagation()}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          "size-7",
          // Open is the usual case, so it fades like a missing asset in the
          // Content column (MeetingAssetIcons' "none").
          restricted ? "text-foreground" : "text-muted-foreground/30",
        )}
        onClick={() => setOpen(true)}
        title={`${translate("resources.zoom_meetings.permission.title")}: ${label}`}
        aria-label={`${translate("resources.zoom_meetings.permission.title")}: ${label}`}
      >
        <Icon className="size-4" />
      </Button>
      {open && (
        <PermissionDialog meeting={meeting} onClose={() => setOpen(false)} />
      )}
    </span>
  );
};

const PermissionDialog = ({
  meeting,
  onClose,
}: {
  meeting: PermissionMeeting;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const [update] = useUpdate();
  const [mode, setMode] = useState<"everyone" | "restricted">(
    meeting.restricted ? "restricted" : "everyone",
  );
  const [selected, setSelected] = useState<Set<Identifier> | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: rows, isSuccess } = useGetList<ZoomMeetingAccess>(
    "zoom_meeting_access",
    {
      filter: { "meeting_ref@eq": meeting.id },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
  );

  // The saved list, once, so a refetch never undoes ticks made since.
  useEffect(() => {
    if (isSuccess && selected == null) {
      setSelected(new Set((rows ?? []).map((row) => row.sales_id)));
    }
  }, [isSuccess, rows, selected]);

  const people = sales.filter((sale) => !sale.disabled);
  const toggle = (id: Identifier, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current ?? []);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleSave = async () => {
    const lock = mode === "restricted";
    setSaving(true);
    try {
      // The list first and the flag last, so a meeting is never locked while
      // its list is half written. Back to Everyone keeps the list, so locking
      // again brings the same people back.
      if (lock && selected) {
        const saved = rows ?? [];
        const savedIds = new Set(saved.map((row) => row.sales_id));
        const toAdd = [...selected].filter((id) => !savedIds.has(id));
        const toRemove = saved
          .filter((row) => !selected.has(row.sales_id))
          .map((row) => row.id);
        await Promise.all(
          toAdd.map((sales_id) =>
            dataProvider.create("zoom_meeting_access", {
              data: { meeting_ref: meeting.id, sales_id },
            }),
          ),
        );
        if (toRemove.length > 0) {
          await dataProvider.deleteMany("zoom_meeting_access", {
            ids: toRemove,
          });
        }
      }
      if (lock !== (meeting.restricted === true)) {
        await update(
          "zoom_meetings",
          {
            id: meeting.id,
            data: { restricted: lock },
            previousData: { id: meeting.id },
          },
          { returnPromise: true },
        );
      }
      await queryClient.invalidateQueries({
        queryKey: ["zoom_meeting_access"],
      });
      onClose();
    } catch {
      notify("resources.zoom_meetings.permission.error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {translate("resources.zoom_meetings.permission.title")}
          </DialogTitle>
          <DialogDescription>
            {translate("resources.zoom_meetings.permission.admins_note")}
          </DialogDescription>
        </DialogHeader>
        <RadioGroup
          value={mode}
          onValueChange={(value) => setMode(value as typeof mode)}
          aria-label={translate("resources.zoom_meetings.permission.title")}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="everyone" id="meeting-permission-everyone" />
            <Label htmlFor="meeting-permission-everyone">
              {translate("resources.zoom_meetings.permission.everyone")}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem
              value="restricted"
              id="meeting-permission-restricted"
            />
            <Label htmlFor="meeting-permission-restricted">
              {translate("resources.zoom_meetings.permission.only_these")}
            </Label>
          </div>
        </RadioGroup>
        {mode === "restricted" && (
          <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto rounded-md border p-3">
            {people.map((sale) => {
              const id = `meeting-permission-sale-${sale.id}`;
              const name =
                `${sale.first_name ?? ""} ${sale.last_name ?? ""}`.trim();
              return (
                <li key={sale.id} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={
                      sale.administrator || (selected?.has(sale.id) ?? false)
                    }
                    disabled={sale.administrator || selected == null}
                    onCheckedChange={(checked) =>
                      toggle(sale.id, checked === true)
                    }
                  />
                  <Label htmlFor={id} className="font-normal">
                    {name}
                    {sale.administrator && (
                      <span className="text-muted-foreground">
                        {" "}
                        {translate(
                          "resources.zoom_meetings.permission.admin_always",
                        )}
                      </span>
                    )}
                  </Label>
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || selected == null}
          >
            {translate("ra.action.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
